import { useMobile } from "../../MobileProvider";
import styled from "@emotion/styled";
import { FC, useEffect, useRef, useState } from "react";
import { getDownloadURL, ref as firestoreRef } from "@firebase/storage";
import { AnimatePresence, useInView } from "framer-motion";
import { logEvent } from "@firebase/analytics";
import TeamItemStyled from "./TeamItemStyled";
import HoverBackground from "./HoverBackground";
import HoverWrapper from "./HoverWrapper";
import Name from "./Name";
import { useApp } from "../../AppProvider";

export type TeamItemProps = {
  video: any;
  name: string;
  position: string;
  email: string;
  order: number;
  id: string;
};

const TeamItemsVideo = styled("video")(() => ({
  width: "100%",
  height: "100%",
  objectFit: "cover",
  willChange: "transform",
}));

const Position = styled("p")(() => ({
  marginTop: "auto",
  color: "#fff",
  textTransform: "uppercase",
}));

const Mail = styled("a")(() => ({
  textTransform: "uppercase",
  color: "rgba(255,255,255,.8)",
  fontSize: 11,
}));

const TeamItem: FC<TeamItemProps> = (props) => {
  const mobile = useMobile();
  const { storage, analytics } = useApp();
  const [show, setShow] = useState<boolean>(false);
  const itemRef = useRef<HTMLDivElement | null>(null);
  const isRootInView = useInView(itemRef);
  const isInView = useInView(itemRef, { margin: "-50% 0% -50% 0%" });
  const [url, setUrl] = useState<string | undefined>(undefined);
  const videoRef = useRef<HTMLVideoElement | null>(null);

  useEffect(() => {
    let cancel = false;

    if (storage && isRootInView && url === undefined) {
      getDownloadURL(firestoreRef(storage, `team/${props.video}`)).then(
        (newUrl) => {
          if (!cancel) {
            setUrl(newUrl);
          }
        }
      );
    }

    return () => {
      cancel = true;
    };
  }, [isRootInView, props.video, storage, url]);

  useEffect(() => {
    if (isInView && mobile) {
      setShow(true);
      if (videoRef.current) {
        if (videoRef.current.paused) {
          videoRef.current.play();
        }
      }
    } else {
      setShow(false);
    }
  }, [isInView, mobile]);

  const handleMouseEnter = () => {
    setShow(true);
    if (analytics) {
      logEvent(analytics, "team_item_mouse_enter", {
        event_label: props.name,
      });
    }
  };

  return (
    <TeamItemStyled
      id={"team"}
      ref={itemRef}
      whileInView={{ y: 0, opacity: 1 }}
      viewport={{ once: true }}
      initial={{ y: "20%", opacity: 0 }}
      transition={{ delay: 0.1, duration: 0.4 }}
      onMouseEnter={handleMouseEnter}
      onMouseLeave={() => setShow(false)}
    >
      <TeamItemsVideo
        ref={videoRef}
        autoPlay
        loop
        playsInline
        muted
        src={url}
      />
      <AnimatePresence>
        {show && (
          <HoverBackground
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            transition={{ delay: mobile ? 0.6 : 0 }}
          />
        )}
      </AnimatePresence>
      <AnimatePresence>
        {show && (
          <HoverWrapper
            initial={{ x: "-100%" }}
            animate={{ x: 0 }}
            exit={{ x: "-100%" }}
            transition={{
              delay: mobile ? 0.7 : 0.1,
              duration: 0.3,
              stiffness: 200,
              damping: 30,
              type: "spring",
            }}
          >
            <Name>{props.name}</Name>
            <Position>{props.position}</Position>
            <Mail href={"mailto:" + props.email}>{props.email}</Mail>
          </HoverWrapper>
        )}
      </AnimatePresence>
    </TeamItemStyled>
  );
};

export default TeamItem;
